import { useState } from 'react';
import { ClaudeCodeSetup } from './ClaudeCodeSetup';

interface PlaintextModalProps {
  plaintext: string;
  keyId: string;
  keyName: string;
  warning: string | null;
  onDismiss: () => void;
}

/**
 * One-time plaintext reveal. The server never stores it and it can never be retrieved.
 * Kept in component state only — never logged, never in URL/title/localStorage.
 */
export function PlaintextModal({ plaintext, keyId, keyName, warning, onDismiss }: PlaintextModalProps) {
  const [copied, setCopied] = useState<'idle' | 'success' | 'failed'>('idle');
  const [acknowledged, setAcknowledged] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(plaintext);
      setCopied('success');
      setTimeout(() => setCopied('idle'), 2000);
    } catch {
      // Clipboard API fails on insecure origins (http://) or when permission is denied
      setCopied('failed');
      setTimeout(() => setCopied('idle'), 3000);
    }
  };

  const handleDismiss = () => {
    if (!acknowledged) {
      alert('Please acknowledge that you have saved the key before dismissing.');
      return;
    }
    if (
      confirm(
        'Are you sure? This key will never be shown again. The server does not store it and cannot retrieve it.',
      )
    ) {
      onDismiss();
    }
  };

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && handleDismiss()}>
      <div className="modal plaintext-modal" role="dialog" aria-labelledby="modal-title">
        <h2 id="modal-title">Key created successfully</h2>

        <div className="callout callout-critical">
          <strong>This is the only time this key will ever be visible.</strong>
          <p>
            The server does not store it and it can never be retrieved. Copy it now and store it
            securely.
          </p>
        </div>

        <div className="form-group">
          <label htmlFor="key-name">Key name</label>
          <div className="mono readonly">{keyName}</div>
        </div>

        <div className="form-group">
          <label htmlFor="plaintext">OpenRouter API key</label>
          <div className="plaintext-box">
            <code className="plaintext-value">{plaintext}</code>
            <button
              type="button"
              onClick={handleCopy}
              className={`copy-button ${copied !== 'idle' ? `copy-${copied}` : ''}`}
              disabled={copied !== 'idle'}
            >
              {copied === 'idle' ? 'Copy' : copied === 'success' ? 'Copied!' : 'Failed'}
            </button>
          </div>
          {copied === 'failed' ? (
            <p className="form-note warn">
              Copy failed. The clipboard API may not be available on this origin. Select and copy
              manually.
            </p>
          ) : null}
        </div>

        {warning ? (
          <div className="callout callout-warning">
            <strong>Warning:</strong> {warning}
          </div>
        ) : null}

        <ClaudeCodeSetup keyId={keyId} plaintext={plaintext} />

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
            I have saved this key in a secure location
          </label>
        </div>

        <div className="form-actions">
          <button type="button" className="primary" onClick={handleDismiss} disabled={!acknowledged}>
            Close
          </button>
        </div>

        <p className="muted small">
          Once you close this dialog, the key cannot be retrieved. If you lose it, you must create a
          new key.
        </p>
      </div>
    </div>
  );
}
